/**
 * Les en-têtes d'un appel authentifié — la définition unique.
 *
 * POURQUOI CE FICHIER. Cette fonction existait en VINGT ET UN exemplaires
 * dans `services/`, un par module, toutes identiques. Le commentaire
 * d'Identite.cfc dit pourquoi c'est dangereux, et cela vaut aussi côté
 * client : « les règles de sécurité qui vivent en plusieurs exemplaires
 * divergent, et c'est la copie oubliée qui ouvre la brèche ». Ici, la copie
 * oubliée ne fait pas entrer un intrus : elle empêche l'application de
 * s'identifier, et l'écran se vide sans qu'aucune erreur ne le signale.
 *
 * LE JETON EST ÉCRIT PAR AuthContext à la connexion, sous `authToken` dans
 * localStorage (ou sessionStorage selon « rester connecté »). AuthContext pose
 * aussi `cabaza_jwt_token` avec la MÊME valeur ; on lit `authToken`, la clé
 * historique que tous les services consomment déjà.
 *
 * L'ABSENCE DE JETON N'EST PAS UNE ERREUR. Un visiteur non connecté consulte
 * légitimement les offres, les livres, les événements : la lecture reste
 * ouverte, c'est le sens même de Cabaza. On rend alors des en-têtes sans
 * `Authorization`, et le serveur décide.
 */

/**
 * Lit le jeton là où AuthContext l'a posé.
 *
 * Le try/catch n'est pas décoratif : en navigation privée ou avec les données
 * de site bloquées, l'accès au stockage LÈVE une exception au lieu de rendre
 * null. Sans lui, un service entier cesserait de fonctionner.
 *
 * @returns {string|null} le jeton, ou null si absent ou illisible
 */
export function lireJeton() {
    try {
        return localStorage.getItem('authToken')
            || sessionStorage.getItem('authToken')
            || null;
    } catch {
        return null;
    }
}

/**
 * Les en-têtes d'un appel qui porte un corps JSON (POST, PUT).
 *
 * @param {boolean} avecCorps false pour un DELETE sans corps, qui n'a pas
 *                            besoin de Content-Type
 * @returns {Object} les en-têtes, avec Authorization si un jeton existe
 */
export function enTetesAuth(avecCorps = true) {
    const jeton = lireJeton();
    const entetes = {};

    if (avecCorps) entetes['Content-Type'] = 'application/json';
    if (jeton) entetes.Authorization = `Bearer ${jeton}`;

    return entetes;
}

/**
 * Alias historique. Les services existants appellent `enTetesEcriture` ; ce
 * nom reste valable pour ne pas avoir à tous les réécrire d'un coup.
 */
export const enTetesEcriture = enTetesAuth;

/**
 * Sommes-nous connectés ?
 *
 * Ne VÉRIFIE pas le jeton — un jeton se vérifie côté serveur, et c'est ce que
 * fait Identite.cfc à chaque appel. Sert seulement à décider d'afficher une
 * action plutôt que de la proposer pour rien.
 *
 * @returns {boolean}
 */
export function estConnecte() {
    return lireJeton() !== null;
}
